import React from "react";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { formatDateTime } from "utils/time-utils";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import {
  TimeOptionKeys,
  TimeOptions,
} from "components/basic/dropdowns/time-unit-dropdown/timeUnits";
import { WeatherMonitoringProps } from "../charts/interfaces";
import {
  getAverageWindDirection,
  getMaxParameter,
  getMinMaxWindSpeed,
} from "./WeatherMonitoringUtils";

const createColumnBody = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  currentStartTime: Date,
  currentEndTime: Date,
  timeInterval: string
) => {
  const temperatureDataArr = datasetsData?.find(
    d => d.identifier === props.temperature?.dataset?.identifier
  )?.data;
  const humidityDataArr = datasetsData?.find(
    d => d.identifier === props.humidity?.dataset?.identifier
  )?.data;
  const precipitationDataArr = datasetsData?.find(
    d => d.identifier === props.precipitation?.dataset?.identifier
  )?.data;
  const windSpeedDataArr = datasetsData?.find(
    d => d.identifier === props.windSpeed?.dataset?.identifier
  )?.data;
  const windDirectionDataArr = datasetsData?.find(
    d => d.identifier === props.windDirection?.dataset?.identifier
  )?.data;

  const maxTemperature = getMaxParameter(
    temperatureDataArr || [],
    currentStartTime,
    currentEndTime,
    "Temperature"
  );
  const { min: minWindSpeed, max: maxWindSpeed } = getMinMaxWindSpeed(
    windSpeedDataArr || [],
    currentStartTime,
    currentEndTime
  );
  const avgWindDirection = getAverageWindDirection(
    windDirectionDataArr || [],
    currentStartTime,
    currentEndTime
  );

  const maxHumidity = getMaxParameter(
    humidityDataArr || [],
    currentStartTime,
    currentEndTime,
    "Humidity"
  );
  const maxPrecipitation = getMaxParameter(
    precipitationDataArr || [],
    currentStartTime,
    currentEndTime,
    "Precipitation"
  );

  const windSpeed = getMaxParameter(
    windSpeedDataArr || [],
    currentStartTime,
    currentEndTime,
    "Wind Speed"
  );

  return (
    <div className="flex flex-column gap-2 font-semibold">
      <div style={{ color: `${props.temperature?.dataset?.line?.color}` }}>{maxTemperature} °C</div>
      <div style={{ color: `${props.humidity?.dataset?.line?.color}` }}>{maxHumidity}%</div>
      <div style={{ color: `${props.precipitation?.dataset?.line?.color}` }}>
        {maxPrecipitation} mm
      </div>
      {timeInterval === TimeOptions.DAYS.name ? (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>
          {minWindSpeed} - {maxWindSpeed} m/s
        </div>
      ) : (
        <div style={{ color: `${props.windSpeed?.dataset?.line?.color}` }}>{windSpeed} m/s</div>
      )}

      <Button
        icon="pi pi-arrow-up"
        disabled
        className="p-button-text p-button-secondary p-0"
        style={{ transform: `rotate(${avgWindDirection}deg)` }}
      />
    </div>
  );
};

const generateColumns = (
  datasetsData: DatasetDataInfoResponse[],
  props: WeatherMonitoringProps,
  timeInterval: string,
  startTime: Date
) => {
  const intervals = Array.from({ length: 24 }, (_, i) => i);
  const totalIntervals = timeInterval === TimeOptions.HOURS.name ? 48 : 7;
  let nextIntervalIndex =
    timeInterval === TimeOptions.DAYS.name
      ? 0
      : intervals.findIndex(interval => interval > startTime.getHours()) - 1;
  nextIntervalIndex = nextIntervalIndex === -1 ? 0 : nextIntervalIndex;

  return Array.from({ length: totalIntervals }, (_, i) => {
    const currentIntervalIndex = (nextIntervalIndex + i) % intervals.length;
    const dayOffset = Math.floor(
      (nextIntervalIndex + i) / (timeInterval === TimeOptions.DAYS.name ? 1 : intervals.length)
    );
    const intervalTime = new Date(startTime);
    const curentStartTime = new Date(startTime);
    intervalTime.setDate(startTime.getDate() + dayOffset);
    curentStartTime.setDate(intervalTime.getDate());

    if (timeInterval === TimeOptions.DAYS.name) {
      intervalTime.setHours(0, 0, 0, 0);
      curentStartTime.setHours(0, 0, 0, 0);
    } else {
      const currentInterval = intervals[currentIntervalIndex];
      intervalTime.setHours(currentInterval, 0, 0, 0);
      curentStartTime.setHours(currentInterval, 0, 0, 0);
    }

    const currentEndTime = new Date(curentStartTime);
    if (timeInterval === TimeOptions.DAYS.name) {
      currentEndTime.setHours(23, 59, 59, 999);
    } else {
      currentEndTime.setHours(curentStartTime.getHours() + 6);
    }

    return (
      <Column
        key={i}
        field={`weatherIcon${i}`}
        style={{ width: timeInterval === TimeOptions.HOURS.name ? "5rem" : "20rem" }}
        header={formatDateTime(
          intervalTime,
          TimeOptions[timeInterval as TimeOptionKeys].timeFormat
        )}
        body={() =>
          createColumnBody(datasetsData, props, curentStartTime, currentEndTime, timeInterval)
        }
      />
    );
  });
};

export default generateColumns;
